// File: frontend/src/components/ui/InfoTip.js
//
// One sentence, on demand, attached to the figure it explains.
//
// ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
//
// Reports carried a permanent info panel above the numbers:
//
//   "Figures are cash — money that actually moved. Equipment, owner withdrawals
//    and transfers between your own accounts are excluded from the operating
//    result. Money owed by agents is not counted until it is collected."
//
// Three problems, and the third is the one that matters:
//
//   · it is read once and then becomes furniture;
//   · it explains four different figures in one block, so the reader has to
//     work out which sentence belongs to the number they are looking at;
//   · it was NOT TRUE of everything below it. Physical voucher sales are
//     recognised when a voucher is first used, which is not when cash moved —
//     an agent may hand the money over weeks later, or never. A standing claim
//     that every figure on the screen is cash is a claim the page cannot keep.
//
// So the explanation moves to the figure. One tip, one number, one sentence,
// and only when the reader asks for it.
//
// ── AND IT HAS TO WORK ON A PHONE ───────────────────────────────────────────
//
// A hover tooltip is not a feature on a touch screen. This opens on TAP, closes
// on tap-away or Escape, and is a real focusable button so a keyboard and a
// screen reader both reach it. The `aria-label` names the subject, because
// "more information" repeated six times down a page tells a screen-reader user
// nothing about which figure they are on.

import React, { useState } from "react";
import { ClickAwayListener, IconButton, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { TYPE_SCALE, tapHitAreaSx } from "./tokens";

export default function InfoTip({
  /** The one sentence. Keep it one sentence. */
  text,
  /** What the sentence is about — used for the accessible name. */
  label,
  /** Nudge the icon's size to sit with the type around it. */
  size = 14,
  sx,
}) {
  const [open, setOpen] = useState(false);
  if (!text) return null;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      {/* The span is the ClickAwayListener's single child AND the Tooltip's
          anchor; without it the listener cannot attach to the IconButton. */}
      <span style={{ display: "inline-flex" }}>
        <Tooltip
          open={open}
          onClose={() => setOpen(false)}
          title={
            <Typography sx={{ fontSize: TYPE_SCALE.meta, lineHeight: 1.45 }}>
              {text}
            </Typography>
          }
          arrow
          placement="top"
          // Controlled, so a tap is the only thing that opens it. Leaving the
          // default hover listeners on would make it flicker open under a
          // thumb that is only scrolling past.
          disableFocusListener
          disableHoverListener
          disableTouchListener
          slotProps={{ tooltip: { sx: { maxWidth: 260, px: 1.25, py: 0.75 } } }}
        >
          <IconButton
            size="small"
            aria-label={label ? `About ${label}` : "More information"}
            aria-expanded={open}
            onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
            onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
            sx={{
              p: 0.25, ml: 0.25, color: "text.disabled",
              "&:hover": { color: "text.secondary", bgcolor: "transparent" },
              ...tapHitAreaSx,
              ...sx,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: size }} />
          </IconButton>
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
}
